import { useRef, useState, useEffect, useCallback } from 'react';
import { throttle } from 'lodash';

import { BreakingNew } from '@/lib/model';
import { DropdownIcon, DropUpIcon } from '../icons';
import { cn } from '@/lib/utils';
import { FromNowDateClient } from '../date/FromNowDateClient';
import { useGetChannelPref } from '@/providers/ChannelProvider';
import { EmblaCarouselType } from 'embla-carousel';
import { TRT_CAROUSEL_BREAKING_NEWS } from '../header/constant';

interface Props extends BreakingNew {
  emblaApi: EmblaCarouselType | null;
  setExpanded: (_: string | undefined) => void;
  expanded?: string;
}

const BREAKING_NEWS_LARGE_SCREEN_MAX_HEIGHT = 120;
const PADDING = 40;

const OffScreenRender = ({
  updatedAt,
  timestamp,
  title,
  description,
  id,
  setExpanded,
  setHeight,
  setIsOverflowing,
  containerRef,
}: Pick<
  Props,
  'updatedAt' | 'timestamp' | 'title' | 'description' | 'id' | 'setExpanded'
> & {
  setHeight: (height: number) => void;
  setIsOverflowing: (height: boolean) => void;
  containerRef: any;
}) => {
  const channelPref = useGetChannelPref();
  const contentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const breakingNewsWrapper = document.getElementById(
      TRT_CAROUSEL_BREAKING_NEWS,
    );
    const onResize = () => {
      setExpanded(undefined);
      const el = contentRef.current;
      const containerEl = containerRef.current;
      if (containerEl) {
        const maxHeight = BREAKING_NEWS_LARGE_SCREEN_MAX_HEIGHT;

        setIsOverflowing(containerEl.scrollHeight > maxHeight - 20);
      }
      if (el) {
        if (breakingNewsWrapper) {
          el.style.width = `${breakingNewsWrapper.getBoundingClientRect().width}px`;
        }
        setHeight(el.clientHeight);
      }
    };

    const throttledOnResize = throttle(onResize, 100);

    onResize();
    window.addEventListener('resize', throttledOnResize);

    return () => window.removeEventListener('resize', throttledOnResize);
  }, [containerRef, setExpanded, setHeight, setIsOverflowing]);

  return (
    <div
      id={`carousel-child-${id}`}
      className="fixed top-[-1000px] w-full grid gap-y-1 items-start z-[-1] opacity-0"
      ref={contentRef}
    >
      <div className="w-full grid relative">
        <div className="font-body text-xs font-extrabold uppercase tracking-[1px] text-white text-nowrap">
          <FromNowDateClient
            date={timestamp || updatedAt}
            languageCode={channelPref?.languageCode}
          />
        </div>
        <div className="font-primary text-lg lg:text-1.5xl xl:text-2xl leading-normal lg:leading-custom_5 xl:leading-8 text-white font-medium">
          {title}
        </div>
      </div>

      <p
        className={cn(
          'block font-body text-xs2 font-light leading-normal text-white ease-in-out duration-200',
        )}
      >
        {description}
      </p>

      <button className="flex justify-center pt-1 mx-auto relative z-10">
        <DropdownIcon className="text-white w-3 h-3" />
      </button>
    </div>
  );
};

export function ExpandableText({
  title,
  description,
  timestamp,
  updatedAt,
  emblaApi,
  setExpanded,
  setSelectedItemHeight,
  expanded,
  id,
}: Props & { setSelectedItemHeight: (height: number) => void }) {
  const channelPref = useGetChannelPref();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);

  const onPlayAgain = useCallback(() => {
    if (!expanded) return;

    const plugins = emblaApi?.plugins();
    if (plugins?.autoplay) {
      plugins.autoplay.options.delay = 3000;
      plugins.autoplay.play();
    }
    setExpanded(undefined);
  }, [emblaApi, setExpanded, expanded]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi?.on('pointerUp', onPlayAgain);
    return () => {
      emblaApi?.off('pointerUp', onPlayAgain);
    };
  }, [emblaApi, onPlayAgain]);

  return (
    <div className="grid relative" ref={containerRef}>
      <div
        id={`carousel-child-${id}`}
        className="w-full h-full grid items-start relative z-[25]"
      >
        <div className="w-full grid relative mb-1">
          <div className="font-body text-xs font-extrabold uppercase tracking-[1px] text-white text-nowrap">
            <FromNowDateClient
              date={timestamp || updatedAt}
              languageCode={channelPref?.languageCode}
            />
          </div>
          <div
            className={cn(
              'font-primary text-lg lg:text-1.5xl xl:text-2xl leading-normal lg:leading-custom_5 xl:leading-8 text-white font-medium',
              {
                'line-clamp-1': !expanded && isOverflowing,
                'line-clamp-none': expanded,
              },
            )}
          >
            {title}
          </div>
        </div>

        <p
          className={cn(
            'block font-body text-xs2 font-light leading-normal text-white ease-in-out duration-200',
            {
              'line-clamp-1 mb-1': isOverflowing && !expanded,
              'opacity-0 h-0 invisible p-0 m-0': !isOverflowing || expanded,
            },
          )}
        >
          {description}
        </p>

        <p
          className={cn(
            'block font-body text-xs2 font-light leading-normal text-white ease-in-out duration-200',
            {
              'opacity-0 h-0 invisible mb-0': isOverflowing && !expanded,
              'opacity-1 h-auto mb-1': !isOverflowing || expanded,
            },
          )}
        >
          {description}
        </p>

        {isOverflowing ? (
          <button
            className={cn(
              'absolute bottom-[-8px] md:bottom-[-12px] left-1/2 transform -translate-x-1/2 flex justify-center pt-1 relative z-10',
              {
                hidden: isOverflowing && expanded,
              },
            )}
            onClick={() => {
              const plugins = emblaApi?.plugins();
              if (plugins?.autoplay) {
                plugins.autoplay.options.delay = 1000 * 1000 * 1000;
                plugins?.autoplay?.stop();
              }
              setExpanded(id);
              setSelectedItemHeight(height + PADDING);
            }}
          >
            <DropdownIcon className="text-white w-3 h-3" />
          </button>
        ) : null}
      </div>

      {isOverflowing && expanded ? (
        <>
          <button
            className="block self-center m-auto pt-2 w-full"
            onClick={onPlayAgain}
          >
            <DropUpIcon className="text-white m-auto" />
          </button>
        </>
      ) : null}

      <OffScreenRender
        updatedAt={updatedAt}
        timestamp={timestamp}
        title={title}
        description={description}
        id={id}
        setHeight={setHeight}
        setExpanded={setExpanded}
        setIsOverflowing={setIsOverflowing}
        containerRef={containerRef}
      />
    </div>
  );
}
